import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { CommencementDateInput, DateInput } from "@/components/common";
import { toDateOnly, toDateTimeLocal, formatDateTimeAsIs, } from "@/utils/dateTime";
import { jobsService } from "@/api/services/jobs.service";
import { useToast } from "@/components/common/Toast";
import styles from "./TradeDateChangeRequestModal.module.css";
export const TradeDateChangeRequestModal = ({ isOpen, onClose, jobId, assignment, initialNewDates, jobTradeId, requestor, requestedById = "", requestedByName = "", onSuccess, }) => {
    const toast = useToast();
    const [reason, setReason] = useState("");
    const [newCommencementDate, setNewCommencementDate] = useState("");
    const [newCommencementTime, setNewCommencementTime] = useState("12:00");
    const [newCompletionDate, setNewCompletionDate] = useState("");
    const [newCompletionTime, setNewCompletionTime] = useState("12:00");
    const [submitting, setSubmitting] = useState(false);
    const prevDate = toDateOnly(assignment?.commencementDate);
    const prevTime = assignment?.commencementTime || "12:00";
    const prevCompDate = toDateOnly(assignment?.completionDate);
    const prevCompTime = assignment?.completionTime || "12:00";
    const prevCommencementDisplay = formatDateTimeAsIs(toDateTimeLocal(prevDate, prevTime));
    const prevCompletionDisplay = formatDateTimeAsIs(toDateTimeLocal(prevCompDate, prevCompTime));
    useEffect(() => {
        if (!isOpen || !assignment)
            return;
        setNewCommencementDate(initialNewDates?.commencementDate ?? prevDate);
        setNewCommencementTime(initialNewDates?.commencementTime ?? prevTime);
        setNewCompletionDate(initialNewDates?.completionDate ?? prevCompDate);
        setNewCompletionTime(initialNewDates?.completionTime ?? prevCompTime);
        setReason("");
    }, [
        isOpen,
        assignment,
        initialNewDates,
        prevDate,
        prevTime,
        prevCompDate,
        prevCompTime,
    ]);
    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget)
            onClose();
    }, [onClose]);
    const handleSubmit = useCallback(async () => {
        if (!assignment)
            return;
        if (!newCommencementDate || !newCompletionDate) {
            toast.error("Please fill in all date fields.", 3000);
            return;
        }
        setSubmitting(true);
        try {
            await jobsService.createTradeDateChangeRequest({
                jobId,
                requestor,
                requestedById: requestedById || undefined,
                requestedByName: requestedByName || undefined,
                tradeAssignmentId: assignment.id,
                jobTradeId: jobTradeId ?? assignment.id ?? undefined,
                previousCommencementDate: toDateTimeLocal(assignment.commencementDate, assignment.commencementTime ?? "12:00") || undefined,
                previousCompletionDate: toDateTimeLocal(assignment.completionDate, assignment.completionTime ?? "12:00") || undefined,
                newCommencementDate: toDateTimeLocal(newCommencementDate, newCommencementTime || "12:00"),
                newCompletionDate: toDateTimeLocal(newCompletionDate, newCompletionTime || "12:00"),
                reason: reason.trim() || undefined,
            });
            toast.success("Trade date change request submitted.", 3000);
            onSuccess();
            onClose();
        }
        catch (err) {
            toast.error("Failed to submit request.", 3000);
        }
        finally {
            setSubmitting(false);
        }
    }, [
        assignment,
        jobId,
        jobTradeId,
        requestor,
        requestedById,
        requestedByName,
        newCommencementDate,
        newCommencementTime,
        newCompletionDate,
        newCompletionTime,
        reason,
        onSuccess,
        onClose,
        toast,
    ]);
    if (!isOpen)
        return null;
    return (_jsx("div", { className: styles.backdrop, onClick: handleBackdropClick, children: _jsxs("div", { className: styles.modal, children: [_jsxs("div", { className: styles.header, children: [_jsx("h2", { className: styles.title, children: "Request Change (Trade Dates)" }), _jsx("button", { className: styles.closeBtn, onClick: onClose, type: "button", children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }), _jsxs("div", { className: styles.content, children: [_jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: "Current trade assigned dates" }), _jsxs("div", { className: styles.readOnlyRow, children: [_jsx("span", { className: styles.readOnlyLabel, children: "Commencement" }), _jsx("span", { className: styles.readOnlyValue, children: prevCommencementDisplay })] }), _jsxs("div", { className: styles.readOnlyRow, children: [_jsx("span", { className: styles.readOnlyLabel, children: "Completion" }), _jsx("span", { className: styles.readOnlyValue, children: prevCompletionDisplay })] })] }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: "Requested new dates" }), _jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, children: "Commencement Date and Time" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: styles.input, value: newCommencementDate, onChange: setNewCommencementDate, disableWeekends: false }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: styles.input, value: newCommencementTime, onChange: (e) => setNewCommencementTime(e.target.value) }) })] })] }), _jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, children: "Completion Date and Time" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: styles.input, value: newCompletionDate, onChange: setNewCompletionDate, onInvalid: () => toast.error("Please enter a valid date.", 4000) }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: styles.input, value: newCompletionTime, onChange: (e) => setNewCompletionTime(e.target.value) }) })] })] })] }), _jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, htmlFor: "trade-date-change-reason", children: "Note (reason for change)" }), _jsx("textarea", { id: "trade-date-change-reason", className: styles.textarea, value: reason, onChange: (e) => setReason(e.target.value), placeholder: "Enter reason for date change...", rows: 3 })] }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { className: styles.cancelBtn, type: "button", onClick: onClose, disabled: submitting, children: "Cancel" }), _jsx("button", { className: styles.submitBtn, type: "button", onClick: handleSubmit, disabled: submitting, children: submitting ? "Submitting..." : "Submit Request" })] })] })] }) }));
};
